import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {injectIntl, defineMessages, FormattedMessage, FormattedRelative, FormattedNumber, IntlShape} from 'react-intl';
import TableDetail from '../fw/TableDetail.react';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import * as BrowserPermission from '../BrowserPermission';
import * as Permission from '../../common/util/Permission';
import filesaver from 'filesaver.js';
import LoadingButton from '../fw/LoadingButton.react';

import types from '../../common/model-api/types';
import contentTypeParser from 'content-type';
import ChannelHeader from '../channels/ChannelHeader.react';
import UserRenderer from '../users/UserRenderer.react';
import {safeJSONparse} from "../../common/util/safeJSONparse";
import {CopyToClipboard} from 'react-copy-to-clipboard';
import {getLastOrganisationId} from '../app/LastOrganisationId';
import xmlFormatter from 'xml-formatter';
import ExportFetchMutation from './ExportFetchMutation.react';
import ExtendFetchExpirationMutation from './ExtendFetchExpirationMutation.react';
import urlMapping from '../app/URLMapping';
import Link from 'react-router/lib/Link';
import mutationError from '../channel-defs/mutationError';
import beautify from 'js-beautify/js/lib/beautify';
import decodeUriComponent from 'decode-uri-component';
import ChannelErrorBox from '../channel-errors/ChannelErrorBox.react';
import SafeHTML from '../fw/SafeHTML.react';
import { Fetch, User } from '../../common/types/types';

const messages = defineMessages({
    title: { id: 'fetchView.title', 'defaultMessage': 'API Requests' },
    hidden: { id: 'fetchView.hidden.title', defaultMessage: 'All authentication information are not stored in logs for security reasons.'},
	serverTimestamp: { id: 'fetchView.timestamp', 'defaultMessage': 'Server time: {time}' },
});

const CLEARED_TEXT = "<!#cleared#!>";

export function convertCleared(intl, text) {
    if (text) {
        let list = [];
        let idx = 0;
        for (let item of text.toString().split(CLEARED_TEXT)) {
            if (idx > 0) {
                list.push(<span className="badge" key={idx++} title={intl.formatMessage(messages.hidden)}><FormattedMessage defaultMessage="hidden" id="fetchView.hidden"/></span>);
            }
            list.push(<SafeHTML key={idx++} value={String(item).replace(/[\u00A0-\u9999<>\&]/gim, (i) => '&#' + i.charCodeAt(0) + ';').replace(new RegExp(/\\n/, 'g'), '<br/>')} />);
        }
        return list;
    }

    return null;
}

function escape(str) {
    if (str) {
        return String(str)
            .replace(new RegExp(/"/, 'g'), '\\"')
            .replace(new RegExp(/\$/, 'g'), '\\$')
            .replace(new RegExp(/\!/, 'g'), '\\!')
            .replace(new RegExp(/\n/, 'g'), '\\n');
    }

    return str;
}

// eslint-disable-next-line max-params
function customColumnRenders(intl, value, column, data, something, type) {
    if (column === 'user') {
        return (<UserRenderer user={data.user} />);
    }

    if (column === 'createdAt') {
        let pureValue = data[column];

        return (
            <span title={intl.formatMessage(messages.serverTimestamp, {time: (new Date(pureValue)).toISOString()})}>
                <FormattedRelative value={pureValue}/>
            </span>
        );
    }

    return value;
}

function constructCURL(request, requestContent) {
    let output = "curl -L -k";

    if (request.method !== 'GET') {
        output += ' -X ' + request.method;
    }

    output += " \"" + escape(request.url) + "\"";

    if (requestContent) {
        output += ' --data-raw "' + escape(requestContent) + '"';
    }

    if (request.requestHeaders) {
        for (let header of Object.keys(request.requestHeaders)) {
            if (header === 'date') {
                continue;
            }
            if (header === 'authorization') {
                continue;
            }

            output += ' -H "' + escape(header) + ': ' + escape(request.requestHeaders[header]) + '"';
        }
    }

    return output;
}

interface FetchRendererProps {
    fetch: Fetch;
    intl: IntlShape;
    relay?: object;
}

export class FetchRenderer extends React.Component<FetchRendererProps> {
    static propTypes = {
        fetch: PropTypes.object.isRequired,
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object,
    };

    constructor(props) {
        super(props);

        this.state = {
            formatRequest: true,
            showAllRequest: false,
            formatResponse: true,
            showAllResponse: false,
            decodeUrl: false
        };
    }

    onClickExport = () => {
        return new Promise((resolve, reject) => {
            let onSuccess = (response) => {
                let blob = new Blob([response.exportFetch.output], {type: response.exportFetch.contentType});
                filesaver.saveAs(blob, response.exportFetch.filename, true);
                resolve();
            };

            this.props.relay.commitUpdate(
                new ExportFetchMutation({
                    fetch: this.props.fetch,
                }), {onSuccess: onSuccess, onFailure: (e) => {
                    mutationError(e);
                    reject(e);
                }}
            );
        });
    };

    onViewExtendExpiration = () => {
        console.log("onViewExtendExpiration", this.props.fetch);
        if (this.props.fetch.expiresAt) {
            
            let expiresAt = new Date(this.props.fetch.expiresAt);
            let createdAt = new Date(this.props.fetch.createdAt);
            let diff = expiresAt.getTime() - createdAt.getTime();

            // continue only if diff is less that 3 months
            if (diff > 0 && diff < 5 * 30 * 24 * 60 * 60 * 1000) {
                return this.onClickExtendExpiration(true);
            }
        }
    }

    onClickExtendExpiration = (onView = false) => {
        return new Promise((resolve, reject) => {
            let onSuccess = (response) => {
                resolve();
            };

            this.props.relay.commitUpdate(
                new ExtendFetchExpirationMutation({
                    fetch: this.props.fetch,
                    onView: onView,
                }), {onSuccess: onSuccess, onFailure: (e) => {
                    mutationError(e);
                    reject(e);
                }}
            );
        });
    };

    onClickExportRequest = (content) => {
        let blob = new Blob([content], {type: "text/plain"});
        filesaver.saveAs(blob, 'request.txt', true);
    };

    showAllResponse = () => {
        let sar = !this.state.showAllResponse;
        this.setState({refreshInProgress: true});
        this.setState({showAllResponse: sar});

        return this.props.relay.setVariables({fetchData: sar}, (state) => {
            if (state.done || state.error) {
                this.setState({refreshInProgress: false});
            }
        });
    };

    onClickDecode = () => {
        if (this.state.decodeUrl === true) {
            this.setState({decodeUrl: false});
        }
        if (this.state.decodeUrl === false) {
            this.setState({decodeUrl: true});
        }
    };

    render() {
        let type = types['fetches'];
        let data = this.props.fetch;
        let intl = this.props.intl;

        this.onViewExtendExpiration();

        let request = {};
        let content = "";
        let originalContent;
        let responseCanBeShortened = false;
        let responseShortened = true;
        let displayedURI = '';

        if (data.request) {
            request = safeJSONparse(data.request);
        }

        if (data.dataPreview) {
            content = data.dataPreview;
        }
        if (data.data) {
            responseShortened = false;
            content = data.data;
        }
        originalContent = content;

        let contentType = '?';
        displayedURI = convertCleared(intl, request.url);

        if (request.headers && request.headers['content-type']) {
            try {
                contentType = contentTypeParser.parse(request.headers['content-type']);
                if (contentType['type'] === "application/json" || contentType['type'] === "text/json") {
                    try {
                        content = beautify.js_beautify(content, {
                            indent_with_tabs: true
                        });
                    } catch (e) {
                        /* ignore error */
                    }
                }
            } catch (e) {
                /* ignore error */
            }
        }

        const maxLength = 500000;
        if (typeof content !== "string") {
            content = JSON.stringify(content, null, 4);
            originalContent = content;

            if (content.length > maxLength) {
                responseCanBeShortened = true;

                if (!this.state.showAllResponse) {
                    responseShortened = true;
                    content = content.substring(0, maxLength) + "...";
                }
            }
        }

        if (content.length > maxLength) {
            responseCanBeShortened = true;
        }

		const isDeveloper = BrowserPermission.isDeveloper();
		const isPartner2 = BrowserPermission.isPartner2();
		const isPartner1 = BrowserPermission.isPartner1();

        let requestContent = request.requestBody;
        if (request.requestHeaders && request.requestHeaders['content-type'] &&  request.requestHeaders['content-type'] === "application/x-www-form-urlencoded") {
            requestContent = decodeURIComponent(requestContent);
        }

        let detailColumns = {'createdAt': {}, 'flowInvocation.createdAt': {linkToView: true}, 'channelDef.name': {}, 'sourceDef.name': {}, 'actionDef.name': {}, 'flow': {
            linkToView: true,
            render: (column, item) => {
                if (data['flow']) {
                    if (data['flow']['name']) {
                        return data['flow']['name'];
                    } else if (data['flow']['flowDef'] && data['flow']['flowDef']['name']) {
                        return data['flow']['flowDef']['name'];
                    }
                }
            },
        }, 'flowDef.name': {linkToView: isDeveloper}, 'flowStepDef.name': {}, 'name': {}, 'success': {}, 'ignoredError': {}, 'errorType': {}, 'error': {}, 'duration': {
            render: (column, item) => {
                return (
                    <span>
                        <FormattedNumber value={item?.[column] || item || 0} />&nbsp;ms {data?.['delay'] > 0 ? <span><span className="fa fa-clock-o" title="" /><FormattedNumber value={item['delay'] / 1000} />&nbsp;s</span> : null}
                    </span>
                );
            }
        }, 'dataLength': {}, 'expiresAt': {}};
        if (data.user) {
            detailColumns['user'] = {};
        }

        if (isDeveloper || isPartner1 || isPartner2) {
            detailColumns['server'] = {};
        }

        let formatType = null;
        if (request && request.headers && request.headers['content-type'] && typeof content === 'string') {
            let contentType = request.headers['content-type'];

            if (contentType.startsWith("text/xml") || contentType.startsWith("application/xml")) {
                formatType = "xml";
            } else if (contentType.startsWith("text/json") || contentType.startsWith("application/json")) {
                formatType = "json";
            }
        } else {
            if (typeof content === 'string') {
                if (content.startsWith("{")) {
                    formatType = "json";
                } else if (content.startsWith("<")) {
                    formatType = "xml";
                }
            }
        }

        let canformatResponse = true;
        if (formatType) {
            if (this.state.formatResponse) {
                if (formatType === "json") {
                    try {
                        content = JSON.stringify(JSON.parse(String(content)), null, 4);
                    } catch (e) {
                        canformatResponse = false;
                        /* ignore error */
                        formatType = null;
                    }
                } else if (formatType === "xml") {
                    try {
                        content = xmlFormatter(String(content), {indentation: '  ', stripComments: false, collapseContent: true});
                    } catch (e) {
                        canformatResponse = false;
                        // eslint-disable-next-line no-console
                        console.error(e);
                        /* ignore error */
                        formatType = null;
                    }
                } else {
                    canformatResponse = false;
                }
            }
        } else {
            canformatResponse = false;
        }
        let contentString = content;
        content = convertCleared(intl, content);
        let originalRequestContent = requestContent;

        let canformatRequest = true;
        if (requestContent && formatType) {
            if (this.state.formatRequest) {
                if (formatType === "json") {
                    let newRequestContent;
                    try {
                        newRequestContent = JSON.stringify(JSON.parse(String(requestContent)), null, 4);
                    } catch (e) {
                        canformatRequest = false;
                        /* ignore error */
                        formatType = null;
                    }

                    if (newRequestContent) {
                        canformatRequest = false;
                        requestContent = newRequestContent;
                    }
                } else if (formatType === "xml") {
                    let newRequestContent;
                    try {
                        newRequestContent = xmlFormatter(String(requestContent), {indentation: '  ', stripComments: false, collapseContent: true});
                    } catch (e) {
                        canformatRequest = false;
                        // eslint-disable-next-line no-console
                        console.error(e);
                        /* ignore error */
                        formatType = null;
                    }

                    if (newRequestContent) {
                        canformatRequest = false;
                        requestContent = newRequestContent;
                    }
                } else {
                    canformatRequest = false;
                }
            }
        } else {
            canformatRequest = false;
        }

        let requestCanBeShortened = false;
        let requestShortened = false;

        if (requestContent && typeof requestContent === 'string' && requestContent.length > maxLength) {
            requestCanBeShortened = true;

            if (!this.state.showAllRequest) {
                requestContent = requestContent.substring(0, maxLength) + "...";
                requestShortened = true;
            }
        }

        let showDecodeUrl = true;
        if (!request.url || decodeUriComponent(request.url) === request.url) {
            showDecodeUrl = false;
        }

        if (this.state.decodeUrl === true) {
            displayedURI = decodeUriComponent(request.url);
        }
        if (this.state.decodeUrl === false) {
            displayedURI = convertCleared(intl, request.url);
        }
        return (<div className="fetchDetail">

            {data.channelErrorDefs && data.channelErrorDefs.edges.length > 0 ? <ChannelErrorBox channelDef={data.channelDef} channelErrorDef={data.channelErrorDefs.edges[0].node} channelErrorMessages={[data.data || data.dataPreview]} /> : null}

            <div className="btn-group-vertical" role="group" style={{float: 'right'}}>
                <CopyToClipboard text={constructCURL(request, originalRequestContent)}>
                    <div className="btn btn-default"><span className="fa fa-clipboard" /> <FormattedMessage defaultMessage="Copy as CURL" id="fetchView.copyAsCurl" /></div>
                </CopyToClipboard>

                {isDeveloper ? <Link className="btn btn-default" to={urlMapping.getUrl(types['fetches'], "retry", data)}><span className="fa fa-repeat"/> <FormattedMessage defaultMessage="Retry request" id="fetch.retryRequest" /></Link> : null}
                {originalRequestContent ? <LoadingButton className="btn btn-default" onClick={() => this.onClickExportRequest(originalRequestContent)}><span className="fa fa-download"/> <FormattedMessage defaultMessage="Download request" id="fetch.exportRequest" /></LoadingButton> : null}
                {data.dataPreview ? <LoadingButton className="btn btn-default" onClick={() => this.onClickExport()}><span className="fa fa-download"/> <FormattedMessage defaultMessage="Download response" id="fetch.export" /></LoadingButton> : null}
                {isDeveloper ? <LoadingButton className="btn btn-default" onClick={() => this.onClickExtendExpiration()}><span className="fa fa-download"/> <FormattedMessage defaultMessage="Keep API for 1 year" id="fetch.extendExpiration" /></LoadingButton> : null}
            </div>


            <TableDetail
                columns={detailColumns}
                data={data}
                metadata={type}
                render={(value, column, data, something, type) => customColumnRenders(intl, value, column, data, something, type)}
            />

            {request && Object.keys(request).length > 0 ? <div>
                <strong><FormattedMessage defaultMessage="Request:" id="fetchView.request"/></strong>{" "}
                <div className='btn-group' style={{display: 'inline-block'}}>
                    {showDecodeUrl ? (this.state.decodeUrl === true ?
                    <LoadingButton className="btn btn-default btn-xs" onClick={() => this.onClickDecode()} style={{float: 'left'}}><span className="fa fa-code" /> <FormattedMessage defaultMessage="Show original URL" id="fetchView.showOriginalUrl" /></LoadingButton> :
                    <LoadingButton className="btn btn-default btn-xs" onClick={() => this.onClickDecode()} style={{float: 'left'}}><span className="fa fa-code" /> <FormattedMessage defaultMessage="Docode URL" id="fetchView.decodeUrl" /></LoadingButton>
                    ) : null}
                </div>
                <pre>
                    {request.method} {displayedURI}<br/>
                    {request.requestHeaders ? <small><table><tbody>{Object.keys(request.requestHeaders).map((header) => {
                        return (
                            <tr key={header}>
                                <th>{header}:</th>
                                <td>{(typeof request.requestHeaders[header] === "object") ? convertCleared(intl, JSON.stringify(request.requestHeaders[header])) : convertCleared(intl, request.requestHeaders[header].toString())}</td>
                            </tr>
                        );
                    })}</tbody></table></small> : null}
                </pre>
            </div> : null}
            {requestContent && requestContent !== CLEARED_TEXT ?
                <div>
                    <strong><FormattedMessage defaultMessage="Content" id="fetchView.content" />:</strong>{" "}
                    <div aria-label="..." className="btn-group" role="group">
                        {canformatRequest ? <button className={"btn btn-default btn-xs " + (this.state.formatRequest ? "active" : "")} onClick={() => this.setState({formatRequest: true})} type="button"><FormattedMessage defaultMessage="Formatted" id="fetchView.formatted" /></button> : null}
                        {canformatRequest ? <button className={"btn btn-default btn-xs " + (this.state.formatRequest ? "" : "active")} onClick={() => this.setState({formatRequest: false})} type="button"><FormattedMessage defaultMessage="Raw" id="fetchView.raw" /></button> : null}
                        {requestCanBeShortened ? <button className={"btn btn-default btn-xs " + (requestShortened ? "" : "active")} onClick={() => this.setState({showAllRequest: !this.state.showAllRequest})} type="button"><FormattedMessage defaultMessage="Full" id="fetchView.full" /></button> : null}
                        <CopyToClipboard text={originalRequestContent}>
                           <div className="btn btn-default btn-xs" style={{float: 'right'}}><span className="fa fa-clipboard" /> <FormattedMessage defaultMessage="Copy" id="fetchView.copy" /></div>
                        </CopyToClipboard>
                    </div>
                    <pre style={{maxHeight: "50em"}}><small>{convertCleared(intl, requestContent)}</small></pre>
                </div>
            : null}


            {request && (request.status || request.headers) ? <div><strong><FormattedMessage defaultMessage="Response:" id="fetchView.response" /></strong><pre>STATUS {request.status}<br/>
                {request.headers ? <small><table><tbody>{Object.keys(request.headers).map((header) => {
                    return (<tr key={header}><th>{header}: </th><td>{convertCleared(intl, request.headers[header])}</td></tr>);
                })}</tbody></table></small> : null}
            </pre></div> : null}
            <strong><FormattedMessage defaultMessage="Content" id="fetchView.content" /> {responseShortened ? <FormattedMessage defaultMessage="(shortened)" id="fetchView.shortened" /> : null}:</strong>{" "}
            {canformatResponse ? <div aria-label="..." className="btn-group" role="group">
                <button className={"btn btn-default btn-xs " + (this.state.formatResponse ? "active" : "")} onClick={() => this.setState({formatResponse: true})} type="button"><FormattedMessage defaultMessage="Formatted" id="fetchView.formatted" /></button>
                <button className={"btn btn-default btn-xs " + (this.state.formatResponse ? "" : "active")} onClick={() => this.setState({formatResponse: false})} type="button"><FormattedMessage defaultMessage="Raw" id="fetchView.raw" /></button>
                {responseCanBeShortened ? <button className={"btn btn-default btn-xs " + (responseShortened ? "" : "active")} onClick={() => this.showAllResponse()} type="button">{this.state.refreshInProgress ? <span className="fa fa-refresh fa-spin"/> : null} <FormattedMessage defaultMessage="Full" id="fetchView.full" /></button> : null}
                {this.state.formatResponse ? <CopyToClipboard text={contentString}>
                        <div className="btn btn-default btn-xs" style={{float: 'right'}}><span className="fa fa-clipboard" /> <FormattedMessage defaultMessage="Copy formatted" id="fetchView.copyFormatted" /></div>
                </CopyToClipboard> : null}
                <CopyToClipboard text={originalContent}>
                        <div className="btn btn-default btn-xs" style={{float: 'right'}}><span className="fa fa-clipboard" /> <FormattedMessage defaultMessage="Copy" id="fetchView.copy" /></div>
                </CopyToClipboard>
            </div> : <div aria-label="..." className="btn-group" role="group">
                {responseCanBeShortened ? <button className={"btn btn-default btn-xs " + (responseShortened ? "" : "active")} onClick={() => this.showAllResponse()} type="button">{this.state.refreshInProgress ? <span className="fa fa-refresh fa-spin"/> : null} <FormattedMessage defaultMessage="Full" id="fetchView.full" /></button> : null}
                <CopyToClipboard text={originalContent}>
                        <div className="btn btn-default btn-xs" style={{float: 'right'}}><span className="fa fa-clipboard" /> <FormattedMessage defaultMessage="Copy" id="fetchView.copy" /></div>
                </CopyToClipboard>
            </div>}
            <pre style={{maxHeight: "50em"}}><small>{content}</small></pre>
        </div>);
    }
}

interface FetchViewProps {
    intl: IntlShape;
    relay?: object;
    user: User;
}

class FetchView extends React.Component<FetchViewProps> {

    typeName = 'fetches';

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object,
        user: PropTypes.object.isRequired,
    };

    render() {
        const data = this.props.user.organisation.channel.fetch;
        let type = types['fetches'];

        return (
            <AppPanel breadcrumbParams={[
                {type, data},
                {type: types['channels'], data: this.props.user.organisation.channel},
                {type: types['organisations'], data: this.props.user.organisation}]
            } title={this.props.intl.formatMessage(messages.title)}>

				<ChannelHeader data={this.props.user.organisation.channel} organisation={this.props.user.organisation}>
					<h3><FormattedMessage defaultMessage="API Request detail:" id="fetches.listDetail" /> {data.sourceDef ? data.sourceDef.name : null}</h3>
				</ChannelHeader>

                <FetchRenderer fetch={data} intl={this.props.intl} relay={this.props.relay} />
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(FetchView), (props) => {
    return props.user.organisation && props.user.organisation.channel && props.user.organisation.channel.fetch;
}), {
    initialVariables: {
        organisationId: '',
        channelId: '',
        fetchId: '',
        fetchData: false,
    },
    prepareVariables (variables) {
		if (!variables.organisationId) {
			variables.organisationId = getLastOrganisationId();
        }

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    realId,
                    name,
                    channel(id: $channelId) {
                        id,
                        name,
                        organisation_id,
                        channelDef {
                            id,
                            name,
							icon
                        },
                        fetch(id: $fetchId) {
                            createdAt,
                            success,
                            id,
                            realId,
                            duration,
                            delay,
                            ignoredError,
                            error,
                            errorType,
                            request,
                            dataPreview,
                            dataLength,
                            expiresAt,
                            data @include(if: $fetchData),
                            channel_id,
                            channelErrorDefs(first: 1) {
                                edges {
                                    node {
                                        id,
                                        name,
                                        title,
                                        regexps,
                                        fieldNames,
                                        description,
                                        resolution,
                                        developersNotes,
                                        type,
                                        resolvableBy,
                                    }
                                }
                            },
                            server,
                            channel {
                                id,
                                name,
                            },
                            channelDef {
                                id,
                                name,
                                icon
                            },
                            organisation_id,
                            organisation {
                                id,
                                name,
                            },
                            actionDef {
                                name,
                                id
                            },
                            sourceDef {
                                name,
                                id
                            },
							user {
								id,
								firstname,
								surname,
								email,
								picture
							},
                            flowInvocation {
                                id,
                                realId,
                                organisation_id,
                                flow_id,
                                createdAt,
                            },
                            flow {
                                name,
                                id,
                                organisation_id,
                                realId,
                                flowDef {
        							id,
        							name,
        						},
                            },
							flowDef {
                                name,
                                id
                            },
							flowStepDef {
                                name,
                                id
                            },
							name
                        }
                    }
                }
            }
        `,
    },
});
